import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Plus, Copy, RefreshCw, Trash2, KeyRound, Power } from 'lucide-react';

export default function ArcaApiExterna() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState([]);
  const [rateLimit, setRateLimit] = useState(60);
  const [cacheHours, setCacheHours] = useState(24);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // {id, token}

  const origin = (typeof window !== 'undefined' ? window.location.origin : '');

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([api.get('/arca/config'), api.get('/arca/tokens')]);
      setConfig(c.data);
      setRateLimit(c.data.rate_limit_per_min ?? 60);
      setCacheHours(c.data.cache_hours ?? 24);
      setTokens(t.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleExternal = async (val) => {
    try {
      const res = await api.put('/arca/config', { external_enabled: val });
      setConfig(res.data);
      toast.success(val ? 'API externa ACTIVADA' : 'API externa DESACTIVADA');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const guardarLimites = async () => {
    try {
      const res = await api.put('/arca/config', { rate_limit_per_min: Number(rateLimit), cache_hours: Number(cacheHours) });
      setConfig(res.data);
      toast.success('Límites guardados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const crearToken = async () => {
    if (!newName.trim()) { toast.error('Poné un nombre al token'); return; }
    setCreating(true);
    try {
      const res = await api.post('/arca/tokens', { nombre: newName });
      setRevealed({ id: res.data.id, token: res.data.token });
      setNewName('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const regenerar = async (id) => {
    if (!window.confirm('Regenerar invalida el token actual. ¿Continuar?')) return;
    try {
      const res = await api.post(`/arca/tokens/${id}/regenerar`);
      setRevealed({ id, token: res.data.token });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleToken = async (id, activo) => {
    try { await api.patch(`/arca/tokens/${id}`, { activo }); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este token?')) return;
    try { await api.delete(`/arca/tokens/${id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const copiar = (txt) => { navigator.clipboard.writeText(txt); toast.success('Copiado'); };

  if (loading) return <div className="p-6"><Loader2 className="animate-spin text-slate-400" /></div>;

  const enabled = !!config?.external_enabled;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-api-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">API Externa ARCA</h1>
        <p className="text-slate-500 text-sm mt-1">Permití que sistemas externos consulten el padrón vía un endpoint protegido por Bearer token.</p>
      </div>

      {/* Switch global */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-2"><Power size={18} /> Estado de la API externa</p>
            <p className="text-sm text-slate-500 mt-1">Cuando está desactivada, el endpoint rechaza todas las peticiones (403).</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={enabled ? 'bg-emerald-600' : 'bg-slate-400'}>{enabled ? 'ACTIVA' : 'DESACTIVADA'}</Badge>
            <Switch checked={enabled} onCheckedChange={toggleExternal} data-testid="arca-external-switch" />
          </div>
        </div>
      </Card>

      {/* Límites */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Límites</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Rate limit (req/min por token)</Label>
            <Input type="number" min={1} value={rateLimit} onChange={(e) => setRateLimit(e.target.value)} className="mt-1" data-testid="arca-rate-input" />
          </div>
          <div>
            <Label>Cache de respuestas (horas, 0 = sin cache)</Label>
            <Input type="number" min={0} value={cacheHours} onChange={(e) => setCacheHours(e.target.value)} className="mt-1" data-testid="arca-cache-input" />
          </div>
        </div>
        <Button className="mt-4" onClick={guardarLimites} data-testid="arca-save-limits">Guardar límites</Button>
      </Card>

      {/* Tokens */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><KeyRound size={18} /> Tokens Bearer</h2>

        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="Nombre del consumidor (ej: Sistema Contable)" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-sm" data-testid="arca-token-name" />
          <Button onClick={crearToken} disabled={creating} data-testid="arca-create-token" className="bg-sky-600 hover:bg-sky-700">
            {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />} Crear token
          </Button>
        </div>

        {revealed && (
          <div className="mb-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="arca-token-revealed">
            <p className="text-sm text-emerald-800 font-medium mb-2">Copiá el token ahora — no se vuelve a mostrar:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border rounded px-3 py-2 text-xs font-mono break-all">{revealed.token}</code>
              <Button size="sm" variant="outline" onClick={() => copiar(revealed.token)}><Copy size={14} className="mr-1" /> Copiar</Button>
            </div>
          </div>
        )}

        {tokens.length === 0 ? (
          <p className="text-sm text-slate-400">No hay tokens creados.</p>
        ) : (
          <div className="rounded-lg border divide-y" data-testid="arca-token-list">
            {tokens.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="font-medium text-slate-800">{t.nombre}</p>
                  <p className="text-xs text-slate-400 font-mono">{t.token_preview}••••••••  ·  {t.last_used_at ? `usado ${new Date(t.last_used_at).toLocaleString()}` : 'sin uso'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={t.activo ? 'bg-emerald-600' : 'bg-slate-400'}>{t.activo ? 'activo' : 'inactivo'}</Badge>
                  <Switch checked={!!t.activo} onCheckedChange={(v) => toggleToken(t.id, v)} data-testid={`arca-token-toggle-${t.id}`} />
                  <Button size="sm" variant="ghost" onClick={() => regenerar(t.id)} title="Regenerar"><RefreshCw size={15} /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => eliminar(t.id)} title="Eliminar"><Trash2 size={15} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Docs */}
      <Card className="p-6" data-testid="arca-api-docs">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Cómo consumirlo</h2>
        <p className="text-sm text-slate-500 mb-3">Todos los endpoints requieren el header <span className="font-mono">Authorization: Bearer &lt;TU_TOKEN&gt;</span> y que la API externa esté ACTIVA.</p>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">1) Padrón (constancia de inscripción) — GET</p>
            <code className="block bg-slate-900 text-slate-100 p-3 rounded-lg text-xs break-all">{`${origin}/api/arca/padron/{CUIT}`}</code>
            <code className="block bg-slate-800 text-slate-100 p-3 rounded-lg text-xs break-all whitespace-pre-wrap mt-1">
{`curl -H "Authorization: Bearer <TU_TOKEN>" \\
  "${origin}/api/arca/padron/20299507107"`}
            </code>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">2) Apócrifos (WSAPOC) — GET</p>
            <code className="block bg-slate-900 text-slate-100 p-3 rounded-lg text-xs break-all">{`${origin}/api/arca/ext/apoc/{CUIT}`}</code>
            <code className="block bg-slate-800 text-slate-100 p-3 rounded-lg text-xs break-all whitespace-pre-wrap mt-1">
{`curl -H "Authorization: Bearer <TU_TOKEN>" \\
  "${origin}/api/arca/ext/apoc/20299507107"`}
            </code>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">3) Constatación de Comprobantes (WSCDC) — POST</p>
            <code className="block bg-slate-900 text-slate-100 p-3 rounded-lg text-xs break-all">{`${origin}/api/arca/ext/wscdc`}</code>
            <code className="block bg-slate-800 text-slate-100 p-3 rounded-lg text-xs break-all whitespace-pre-wrap mt-1">
{`curl -X POST -H "Authorization: Bearer <TU_TOKEN>" -H "Content-Type: application/json" \\
  "${origin}/api/arca/ext/wscdc" \\
  -d '{"cbte_modo":"CAE","cuit_emisor":"30123456789","pto_vta":1,"cbte_tipo":1,"cbte_nro":123,"cbte_fch":"20260615","imp_total":12100.00,"cod_autorizacion":"74123456789012"}'`}
            </code>
          </div>
        </div>
      </Card>
    </div>
  );
}
